import {zodResolver} from '@hookform/resolvers/zod';
import {makePostHealthRequestSchedule} from 'main/factories/usecases/private/booking-flow/remote-health-booking/post-health-request-schedule-factory';
import {HomeEnum} from 'presentation/constants';
import {usePersonalData} from 'presentation/hooks';
import {EntryPointCodeEnum} from 'presentation/pages/private/booking/constants';

import {useQueryParams} from 'presentation/pages/private/hooks';
import {useState} from 'react';
import {useForm} from 'react-hook-form';
import {useNavigate} from 'react-router-dom';
import {useQueryClient} from '@tanstack/react-query';
import {QUERY_KEYS} from 'infra/libs/react-query/queryKeys';
import {
	AvailabilityEnum,
	REQUEST_SCHEDULE_DEFAULT_VALUES,
	TypeOfConsultationEnum,
} from '../constants';
import {processShift} from '../utils/processShift';
import {
	requestScheduleSchema,
	RequestScheduleSchema,
} from '../validation/schemas/RequestScheduleSchema';

export function useRequestScheduleLayout() {
	const navigate = useNavigate();
	const {currentEmail, currentPhone} = usePersonalData();
	const {SEARCH_PARAMS} = useQueryParams();
	const [errorState, setErrorState] = useState(false);
	const [successState, setSuccessState] = useState(false);
	const [successProtocolState, setSuccessProtocolState] = useState('');
	const queryClient = useQueryClient();

	const {subSpecialtyCode, specialtyCode, ufCode, requestSchedulePermissionCity} = SEARCH_PARAMS;

	const methods = useForm<RequestScheduleSchema>({
		resolver: zodResolver(requestScheduleSchema),
		mode: 'onChange',
		defaultValues: REQUEST_SCHEDULE_DEFAULT_VALUES,
	});

	const {
		watch,
		setValue,
		handleSubmit,
		formState: {isSubmitting},
	} = methods;

	const watchedData = watch();

	const handleCloseErrorModal = () => setErrorState(false);

	const handleConfirm = async (data: RequestScheduleSchema) => {
		if (isSubmitting) return;
		setErrorState(false);

		const shiftException =
			data.availability === AvailabilityEnum.RESTRICTION
				? {
						morning: processShift(data.morning),
						afternoon: processShift(data.afternoon),
						night: processShift(data.night),
					}
				: {};

		const acceptsTele = data.typeOfConsultation === TypeOfConsultationEnum.TELE_OR_IN_PERSON;

		try {
			const {protocolo} = await makePostHealthRequestSchedule().post({
				specialtyCode: Number(specialtyCode),
				entryPointCode: EntryPointCodeEnum.V1,
				subSpecialtyCode: subSpecialtyCode ? Number(subSpecialtyCode) : undefined,
				stateCode: ufCode,
				mobile: currentPhone,
				email: currentEmail,
				shiftException,
				acceptsTele,
				acceptsWhatsappContact: data.whatsappUpdates,
				anyDayTime: data.availability === AvailabilityEnum.ANY_DAY,
				cityName: requestSchedulePermissionCity,
				phoneNumber: currentPhone,
			});

			// Quando implementar useMutation na chamada acima, invalidar o cache com a func abaixo no onSuccess

			queryClient.invalidateQueries({
				queryKey: [QUERY_KEYS.CONSULT_PROTOCOLS],
			});
			setSuccessProtocolState(protocolo);
			setSuccessState(true);
		} catch (error) {
			setErrorState(true);
		}
	};

	const handleGoToHome = () => {
		navigate(HomeEnum.HOME_PAGE, {replace: true});
	};

	return {
		handleConfirm,
		handleCloseErrorModal,
		watchedData,
		setValue,
		handleSubmit,
		errorState,
		successProtocolState,
		successState,
		methods,
		currentPhone,
		currentEmail,
		isSubmitting,
		handleGoToHome,
	};
}
